<template>
  <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
    <div class="flex items-center justify-between mb-5">
      <h2 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
        <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        Your Files
        <span v-if="files.length > 0" class="text-sm font-normal text-gray-400">({{ files.length }})</span>
      </h2>
      <button
        class="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        :disabled="isLoading"
        @click="emit('refresh')"
      >
        <svg
          class="w-4 h-4 transition-transform duration-500"
          :class="{ 'animate-spin': isLoading }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Refresh
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading && files.length === 0" class="py-16 text-center">
      <svg class="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-gray-500 text-sm">Loading files…</p>
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="py-10 text-center">
      <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
        <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <p class="text-red-600 font-medium mb-1">Failed to load files</p>
      <p class="text-gray-500 text-sm">{{ errorMessage }}</p>
      <button
        class="mt-3 text-sm text-blue-600 hover:underline"
        @click="emit('refresh')"
      >Try again</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="files.length === 0" class="py-16 text-center">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      </div>
      <p class="text-gray-600 font-medium">No files yet</p>
      <p class="text-gray-400 text-sm mt-1">Upload your first file to get started</p>
    </div>

    <!-- File List -->
    <div v-else class="space-y-3">
      <div
        v-for="file in files"
        :key="file.fileId"
        class="group flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition-all duration-150"
      >
        <!-- File Icon -->
        <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-white shadow-sm border border-gray-200 flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>

        <!-- File Info -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-800 truncate" :title="file.fileName">
            {{ file.fileName }}
          </p>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ formatSize(file.fileSize) }}
            <span class="mx-1.5 text-gray-300">·</span>
            {{ formatDate(file.uploadedAt) }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-1 flex-shrink-0">
          <!-- Download Button -->
          <button
            class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            title="Download file"
            @click="downloadFile(file)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>

          <!-- Copy Share Link Button -->
          <button
            class="p-2 rounded-lg transition-colors"
            :class="copiedId === file.fileId
              ? 'text-green-600 bg-green-100'
              : 'text-gray-400 hover:text-purple-600 hover:bg-purple-100'"
            :title="copiedId === file.fileId ? 'Copied!' : 'Copy share link'"
            @click="copyShareLink(file)"
          >
            <svg v-if="copiedId !== file.fileId" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>

          <!-- Delete Button -->
          <button
            class="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            :class="{ 'opacity-50 cursor-not-allowed': deletingId === file.fileId }"
            :disabled="deletingId === file.fileId"
            title="Delete file"
            @click="confirmDelete(file)"
          >
            <svg v-if="deletingId !== file.fileId" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="deleteTarget"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="deleteTarget = null"
      >
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 z-10">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-gray-900">Delete File</h3>
              <p class="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>
          <p class="text-sm text-gray-700 mb-6">
            Are you sure you want to delete
            <span class="font-semibold">"{{ deleteTarget.fileName }}"</span>?
            The file and all its share links will stop working.
          </p>
          <div class="flex gap-3">
            <button
              class="flex-1 py-2 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              @click="deleteTarget = null"
            >
              Cancel
            </button>
            <button
              class="flex-1 py-2 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              @click="executeDelete"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { deleteFile, getDownloadInfo } from '../api.js';

const props = defineProps({
  files: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['refresh', 'deleted']);

const copiedId = ref(null);
const deletingId = ref(null);
const deleteTarget = ref(null);

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(isoString) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function downloadFile(file) {
  try {
    const { downloadUrl } = await getDownloadInfo(file.shareToken);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = file.fileName;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    alert(`Download failed: ${error.message}`);
  }
}

async function copyShareLink(file) {
  const shareUrl = `${window.location.origin}/share/${file.shareToken}`;
  try {
    await navigator.clipboard.writeText(shareUrl);
    copiedId.value = file.fileId;
    setTimeout(() => {
      copiedId.value = null;
    }, 2000);
  } catch {
    // Fallback for browsers that don't support clipboard API
    const textArea = document.createElement('textarea');
    textArea.value = shareUrl;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    copiedId.value = file.fileId;
    setTimeout(() => {
      copiedId.value = null;
    }, 2000);
  }
}

function confirmDelete(file) {
  deleteTarget.value = file;
}

async function executeDelete() {
  if (!deleteTarget.value) return;
  const file = deleteTarget.value;
  deleteTarget.value = null;
  deletingId.value = file.fileId;

  try {
    await deleteFile(file.fileId);
    emit('deleted', file.fileId);
    emit('refresh');
  } catch (error) {
    alert(`Delete failed: ${error.message}`);
  } finally {
    deletingId.value = null;
  }
}
</script>
